import { z } from 'zod'
import { emailSchema, paginationSchema } from './common'

/**
 * Marketing email consent. A contact is one email address; lists are topics (newsletter, festival,
 * volunteer …). Consent never depends on an account and never touches receipts or other
 * transactional email.
 */

export const MARKETING_CONTACT_STATUSES = [
  'subscribed',
  'unsubscribed',
  'bounced',
  'complained',
] as const
export type MarketingContactStatus = (typeof MARKETING_CONTACT_STATUSES)[number]

/** The opt-in checkbox label, the same on every form. */
export const MARKETING_OPT_IN_LABEL = 'Send me BTown Pride news and updates (unsubscribe any time)'

/** What people can tick on the "2027: get notified" page. Stored in the subscription source. */
export const FESTIVAL_INTERESTS = ['sponsor', 'vendor', 'parade', 'volunteer', 'updates'] as const
export type FestivalInterest = (typeof FESTIVAL_INTERESTS)[number]
export const FESTIVAL_INTEREST_LABELS: Record<FestivalInterest, string> = {
  sponsor: 'I want to sponsor',
  vendor: 'I want to be a vendor',
  parade: 'I want to march in the parade',
  volunteer: 'I want to volunteer',
  updates: 'Just keep me posted',
}

/** `festival-2027:sponsor,vendor` → { year: 2027, interests: ['sponsor', 'vendor'] }. */
export function parseFestivalSource(
  source: string | null | undefined,
): { year: number; interests: FestivalInterest[] } | null {
  const m = /^festival-(\d{4}):?(.*)$/.exec(source ?? '')
  if (!m) return null
  const interests = (m[2] ?? '')
    .split(',')
    .filter((i): i is FestivalInterest => (FESTIVAL_INTERESTS as readonly string[]).includes(i))
  return { year: Number(m[1]), interests }
}

const blankToNull = (v: unknown) => (typeof v === 'string' && v.trim() === '' ? null : v)

export const listKeySchema = z
  .string()
  .min(2)
  .max(40)
  .regex(/^[a-z0-9]+(?:[-_][a-z0-9]+)*$/, 'Use lowercase letters, numbers and dashes')

const firstNameSchema = z.preprocess(blankToNull, z.string().trim().max(100).nullable().optional())

/** Forms that offer the opt-in send this alongside their own fields. */
export const marketingOptInField = { marketingOptIn: z.boolean().optional() }

/** Footer signup and similar public forms. `homepage` is a honeypot people never see. */
export const publicSubscribeSchema = z.object({
  email: emailSchema,
  firstName: firstNameSchema,
  lists: z.array(listKeySchema).max(10).optional(),
  homepage: z.string().max(200).optional(),
})
export type PublicSubscribe = z.infer<typeof publicSubscribeSchema>

/** The "Pride on the Riverfront 2027 — be the first to know" page. */
export const festivalInterestSchema = z.object({
  email: emailSchema,
  firstName: firstNameSchema,
  year: z.number().int().min(2026).max(2100),
  interests: z
    .array(z.enum(FESTIVAL_INTERESTS))
    .min(1, 'Choose at least one option')
    .max(FESTIVAL_INTERESTS.length),
  homepage: z.string().max(200).optional(),
})
export type FestivalInterestInput = z.infer<typeof festivalInterestSchema>

/** Manage preferences (by emailed link or in the portal): lists on/off, or leave everything. */
export const preferencesUpdateSchema = z.object({
  lists: z.record(listKeySchema, z.boolean()).optional(),
  unsubscribeAll: z.boolean().optional(),
})
export type PreferencesUpdate = z.infer<typeof preferencesUpdateSchema>

/** The emailed "Confirm you want BTown Pride news" link (a signed, expiring token). */
export const marketingConfirmSchema = z.object({ token: z.string().min(10).max(2000) })
export interface MarketingConfirmResult {
  /** False when the address can't be signed up from a link (it reported our mail as spam). */
  ok: boolean
}

export interface MarketingPreferences {
  /** Masked for emailed links (`j•••@example.com`); in full in the portal. */
  email: string
  firstName: string | null
  status: MarketingContactStatus | 'none'
  lists: { key: string; name: string; description: string | null; subscribed: boolean }[]
}

// ---- Admin ---------------------------------------------------------------------------------

export const marketingListInputSchema = z.object({
  key: listKeySchema,
  name: z.string().trim().min(2).max(120),
  description: z.preprocess(blankToNull, z.string().trim().max(400).nullable().optional()),
  isPublic: z.boolean().default(true),
  active: z.boolean().default(true),
  sortOrder: z.number().int().min(0).max(1000).optional(),
})
export type MarketingListInput = z.infer<typeof marketingListInputSchema>

export interface MarketingListView {
  id: string
  key: string
  name: string
  description: string | null
  isPublic: boolean
  active: boolean
  sortOrder: number
  subscribers: number
  unsubscribed: number
}

export const adminContactInputSchema = z.object({
  email: emailSchema,
  firstName: firstNameSchema,
  lists: z.array(listKeySchema).min(1, 'Choose at least one list').max(20),
})
export type AdminContactInput = z.infer<typeof adminContactInputSchema>

export const contactsQuerySchema = paginationSchema.extend({
  q: z.string().trim().max(120).optional(),
  list: listKeySchema.optional().or(z.literal('').transform(() => undefined)),
  status: z
    .enum(MARKETING_CONTACT_STATUSES)
    .optional()
    .or(z.literal('').transform(() => undefined)),
  interest: z
    .enum(FESTIVAL_INTERESTS)
    .optional()
    .or(z.literal('').transform(() => undefined)),
})
export type ContactsQuery = z.infer<typeof contactsQuerySchema>

export interface MarketingContactRow {
  id: string
  email: string
  firstName: string | null
  status: MarketingContactStatus
  source: string | null
  consentAt: string | null
  unsubscribedAt: string | null
  createdAt: string
  personId: string | null
  /** The linked person is in discreet mode: newsletters go out with the neutral sender. */
  discreet: boolean
  lists: { key: string; status: string; source: string | null }[]
  /** Festival interests from the "get notified" page, when any. */
  interests: FestivalInterest[]
}
